import React from 'react';
import { 
  profile_messages_loadingProjects,
  profile_messages_loadingCourses,
  profile_noProjectsYet,
  profile_noCoursesYet,
  profile_tabNotImplemented
} from '@paraglide/messages';
import { TweetList } from './TweetList';
import { ComposeModal } from "@edusense/features-tweet-composer";
import { RoadmapList } from "@edusense/roadmap-ui";
import { ProjectList } from "@edusense/project-ui";
import { CourseList } from "@edusense/course-ui";
import { StatusDisplay } from '@edusense/shared-ui-kit';
import { ProfileTabType, Tweet } from '../base';
import { TabContentWrapper, EmptyStateWrapper, EmptyStateIcon, EmptyStateText } from '../Profile.styles';
import ProfileRoadmapsTab from './ProfileRoadmapsTab';
import ProfileCoursesTab from './ProfileCoursesTab';

// 프로필 콘텐츠 영역을 담당하는 컴포넌트
interface ProfileContentProps {
  activeTab: ProfileTabType;
  profile: any;
  isAuthenticated: boolean;
  isProfileOwner: boolean;
  tweets: Tweet[];
  tweetsLoading: boolean;
  userProjects: any[];
  userCourses: any[];
  userRoadmaps: any[];
  showComposeModal: boolean;
  replyToTweet: any;
  projectsLoading: boolean;
  coursesLoading: boolean;
  roadmapsLoading: boolean;
  hasMoreProjects: boolean;
  hasMoreCourses: boolean;
  hasMoreRoadmaps: boolean;
  onComment: (id: string) => void;
  onTweetSubmit: (data: any) => Promise<boolean>;
  onCloseModal: () => void;
  onLoadMoreProjects: () => void;
  onLoadMoreCourses: () => void;
  onLoadMoreRoadmaps: () => void;
  handleEditProject: (id: string) => void;
  handleDeleteProject: (id: string) => void;
  handleDuplicateProject: (id: string) => void;
  handleShareProject: (id: string) => void;
  handleReportProject: (id: string) => void;
  handleViewProjectDetails: (id: string) => void;
  handleViewRoadmapDetails: (id: string) => void;
  handleEditRoadmap: (id: string) => void;
  handleDeleteRoadmap: (id: string) => void;
  refreshTweets?: () => void;
}

export const ProfileContent: React.FC<ProfileContentProps> = ({
  activeTab,
  profile,
  isAuthenticated,
  isProfileOwner,
  tweets,
  tweetsLoading,
  userProjects,
  userCourses,
  userRoadmaps,
  showComposeModal,
  replyToTweet,
  projectsLoading,
  coursesLoading,
  roadmapsLoading,
  hasMoreProjects,
  hasMoreCourses,
  hasMoreRoadmaps,
  onComment,
  onTweetSubmit,
  onCloseModal,
  onLoadMoreProjects,
  onLoadMoreCourses,
  onLoadMoreRoadmaps,
  handleEditProject,
  handleDeleteProject,
  handleDuplicateProject,
  handleShareProject,
  handleReportProject,
  handleViewProjectDetails,
  handleViewRoadmapDetails,
  handleEditRoadmap,
  handleDeleteRoadmap,
  refreshTweets
}) => {

  const profileData = (profile as any)?.basicInfo ? profile : (profile as any)?.data ?? profile;
  const profileOwnerId = profileData?.basicInfo?.id ?? profileData?.userId ?? profileData?.id;
  const profileOwnerUsername = (profileData?.basicInfo?.username ?? profileData?.username ?? profileData?.userName ?? '') as string;

  const renderTabContent = () => {
    switch (activeTab) {
      case 'tweets':
        return (
          <>
            <TweetList
              tweets={tweets}
              loading={tweetsLoading}
              onComment={onComment}
              canComment={isAuthenticated}
              showInteractionButtons={true}
              onLikeToggle={() => {}}
              onRetweetToggle={() => {}}
              onBookmarkToggle={() => {}}
              onDelete={() => {}}
            />
            {showComposeModal && (
              <ComposeModal
                isOpen={showComposeModal}
                onClose={onCloseModal}
                onSubmit={onTweetSubmit}
                replyToTweet={replyToTweet}
                refreshTweets={refreshTweets}
              />
            )}
          </>
        );

      case 'projects':
        if (projectsLoading && userProjects.length === 0) {
          return <StatusDisplay type="loading" message={profile_messages_loadingProjects()} />;
        }
        if (userProjects.length === 0) {
          return (
            <EmptyStateWrapper>
              <EmptyStateIcon />
              <EmptyStateText>{profile_noProjectsYet()}</EmptyStateText>
            </EmptyStateWrapper>
          );
        }
        return (
          <ProjectList
            projects={userProjects}
            loading={projectsLoading}
            hasMore={hasMoreProjects}
            onLoadMore={onLoadMoreProjects}
            onEdit={handleEditProject}
            onDelete={handleDeleteProject}
            onDuplicate={handleDuplicateProject}
            onShare={handleShareProject}
            onReport={handleReportProject}
            onViewDetails={handleViewProjectDetails}
          />
        );

      case 'learning':
        if (coursesLoading && userCourses.length === 0) {
          return <StatusDisplay type="loading" message={profile_messages_loadingCourses()} />;
        }
        if (userCourses.length === 0) {
          return (
            <EmptyStateWrapper>
              <EmptyStateIcon />
              <EmptyStateText>{profile_noCoursesYet()}</EmptyStateText>
            </EmptyStateWrapper>
          );
        }
        return (
          <CourseList
            courses={userCourses}
            loading={coursesLoading}
            hasMore={hasMoreCourses}
            onLoadMore={onLoadMoreCourses}
          />
        );

      case 'teaching':
        return (
          <ProfileCoursesTab 
            key="teaching-tab"
            userId={profileOwnerId != null ? String(profileOwnerId) : ''}
            ownerUsername={profileOwnerUsername || undefined}
            isMyProfile={isProfileOwner}
            mode="created"
          />
        );

      case 'roadmaps':
        return (
          <ProfileRoadmapsTab
            roadmaps={userRoadmaps}
            loading={roadmapsLoading}
            hasMore={hasMoreRoadmaps}
            onLoadMore={onLoadMoreRoadmaps}
            onViewDetails={handleViewRoadmapDetails}
            onEdit={handleEditRoadmap}
            onDelete={handleDeleteRoadmap}
          />
        );

      default:
        return (
          <EmptyStateWrapper>
            <EmptyStateIcon />
            <EmptyStateText>{profile_tabNotImplemented()}</EmptyStateText>
          </EmptyStateWrapper>
        );
    }
  };

  return (
    <TabContentWrapper>
      {renderTabContent()}
    </TabContentWrapper>
  );
};
